/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationUserSessionIssuerMigratePreflightRequest,
  GetOrganizationUserSessionIssuerMigratePreflightSecurity,
} from "../models/operations/getorganizationusersessionissuermigratepreflight.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuerMigratePreflightQuery,
  OrganizationUserSessionIssuerMigratePreflightQueryData,
  prefetchOrganizationUserSessionIssuerMigratePreflight,
  queryKeyOrganizationUserSessionIssuerMigratePreflight,
} from "./organizationUserSessionIssuerMigratePreflight.core.js";
export {
  buildOrganizationUserSessionIssuerMigratePreflightQuery,
  type OrganizationUserSessionIssuerMigratePreflightQueryData,
  prefetchOrganizationUserSessionIssuerMigratePreflight,
  queryKeyOrganizationUserSessionIssuerMigratePreflight,
};

export type OrganizationUserSessionIssuerMigratePreflightQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getIssuerMigratePreflight organizationUserSessionIssuers
 *
 * @remarks
 * Report the impact, blockers, and configuration warnings for consolidating one user_session_issuer onto another. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMigratePreflight(
  request: GetOrganizationUserSessionIssuerMigratePreflightRequest,
  security?:
    | GetOrganizationUserSessionIssuerMigratePreflightSecurity
    | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuerMigratePreflightQueryData,
    OrganizationUserSessionIssuerMigratePreflightQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuerMigratePreflightQueryData,
  OrganizationUserSessionIssuerMigratePreflightQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuerMigratePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getIssuerMigratePreflight organizationUserSessionIssuers
 *
 * @remarks
 * Report the impact, blockers, and configuration warnings for consolidating one user_session_issuer onto another. Requires org:read.
 */
export function useOrganizationUserSessionIssuerMigratePreflightSuspense(
  request: GetOrganizationUserSessionIssuerMigratePreflightRequest,
  security?:
    | GetOrganizationUserSessionIssuerMigratePreflightSecurity
    | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuerMigratePreflightQueryData,
    OrganizationUserSessionIssuerMigratePreflightQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuerMigratePreflightQueryData,
  OrganizationUserSessionIssuerMigratePreflightQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuerMigratePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationUserSessionIssuerMigratePreflightData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      sourceId: string;
      targetId: string;
      gramSession?: string | undefined;
    },
  ],
  data: OrganizationUserSessionIssuerMigratePreflightQueryData,
): OrganizationUserSessionIssuerMigratePreflightQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuerMigratePreflight(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationUserSessionIssuerMigratePreflightQueryData
  >(key, data);
}

export function invalidateOrganizationUserSessionIssuerMigratePreflight(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      sourceId: string;
      targetId: string;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "getMigratePreflight",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuerMigratePreflight(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "getMigratePreflight",
    ],
  });
}
